import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { ThemeProvider } from './context/ThemeContext';

// Global: force ALL text inputs to uppercase so React state is always uppercase
const SKIP_TYPES = new Set(['password', 'email', 'number', 'tel', 'file', 'checkbox', 'radio', 'hidden', 'color', 'range', 'date', 'time', 'datetime-local', 'month', 'week', 'url']);

// Use React's internal setter so onChange fires correctly without causing double-update bugs
const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;

document.addEventListener('input', (e) => {
  if (e._isUppercaseEvent) return; // prevent infinite loop
  const el = e.target;
  const isText = el.tagName === 'INPUT' && !SKIP_TYPES.has(el.type);
  const isTextArea = el.tagName === 'TEXTAREA';
  if ((!isText && !isTextArea) || el.classList.contains('no-uppercase') || el.id === 'password' || el.id === 'username') return;

  const upper = el.value.toUpperCase();
  if (el.value === upper) return; // already uppercase, nothing to do

  const start = el.selectionStart;
  const end = el.selectionEnd;

  // Use React's internal setter to properly trigger React's onChange
  if (isText && nativeInputValueSetter) {
    nativeInputValueSetter.call(el, upper);
  } else if (isTextArea && nativeTextAreaValueSetter) {
    nativeTextAreaValueSetter.call(el, upper);
  } else {
    el.value = upper;
  }

  // Restore cursor position
  try { el.setSelectionRange(start, end); } catch {}

  // Fire a React-compatible change event
  const ev = new Event('input', { bubbles: true });
  ev._isUppercaseEvent = true;
  el.dispatchEvent(ev);
}, true);

const CLICK_THROTTLE_MS = 900;
const clickableSelector = [
  'button',
  'a[href]',
  'input[type="button"]',
  'input[type="submit"]',
  'input[type="reset"]',
  '[role="button"]',
  '[data-clickable="true"]',
  '.btn',
  '.dropdown-item',
].join(',');
const lastClickAt = new WeakMap();

document.addEventListener('click', (event) => {
  const target = event.target?.closest?.(clickableSelector);
  if (!target || target.dataset.noThrottle === 'true') return;
  if (target.disabled || target.getAttribute('aria-disabled') === 'true') return;

  const now = Date.now();
  const last = lastClickAt.get(target) || 0;

  if (now - last < CLICK_THROTTLE_MS) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    return;
  }

  lastClickAt.set(target, now);
  target.classList.remove('ritika-clicked');
  window.requestAnimationFrame(() => {
    target.classList.add('ritika-clicked');
    window.setTimeout(() => target.classList.remove('ritika-clicked'), 260);
  });
}, true);

const decorateRitikaSpinners = (root = document) => {
  root.querySelectorAll?.('.spinner-border, .spinner-grow')?.forEach((spinner) => {
    if (spinner.querySelector(':scope > .ritika-loader-logo-img')) return;
    const img = document.createElement('img');
    img.className = 'ritika-loader-logo-img';
    img.src = '/logo.jpeg';
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    spinner.appendChild(img);
  });
};

const spinnerObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches?.('.spinner-border, .spinner-grow')) {
        decorateRitikaSpinners(node.parentElement || document);
      } else {
        decorateRitikaSpinners(node);
      }
    });
  });
});

decorateRitikaSpinners();
spinnerObserver.observe(document.documentElement, { childList: true, subtree: true });

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </React.StrictMode>
);
